import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import OffCanvasTray from "../OffCanvasTray";
import CellarBtnCta from "../../atoms/CellarBtnCta";
import AnimatingTrayContainer from "../../atoms/AnimatingTrayContainer";
import WineTagsShareAndBookmark from "../../atoms/WineTagsShareAndBookmark";
import { GridHolder } from "../../molecules/GridHolder";
import Title from "../../atoms/Title";
import BuyAgainButtons from "../../molecules/BuyAgainButtons";
import WineDetailsTray from "../WineDetailsTray";
import WineListsSidePanel from "../WineListsSidePanel";

import { GET_MEMBER_SHOPPING_CART } from "../../../graphql/resolvers/member";
import { GetLatestVintage_latestVintage } from "../../../types/__generated__/GetLatestVintage";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";

import backArrow from "../../../assets/images/icons/arrow-left-straight.svg";
import cellarBottleImg from "../../../assets/images/temp/bottle-xl-vertical.png";
import { getWineBaseColor } from "../../../helpers/tools";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { useAppContext, useTopInsetBg } from "../Layout/context";

import { CellarBottleHolder } from "../../atoms/CellarBottleHolder";
import RotatingBottleBox from "../../atoms/RotatingBottleBox";
import { BodySection, BodySectionContent, ImageSection, CellarBodyCloseBtnImageSection } from "./style";

interface VintageOptionProps {
  wine: GetLatestVintage_latestVintage;
  onCloseTray: () => void;
  isInBoxAndCartTray?: boolean;
}

const WineCellarVintageOptionTray = ({ wine, onCloseTray, isInBoxAndCartTray }: VintageOptionProps) => {
  const { memberId } = useAppContext();
  const { t } = useTranslation([I18N_NAMESPACES.MY_CELLAR]);
  const history = useHistory();
  const [isShowing, setIsShowing] = useState(true);
  const [isWineDetailsCanvasOpened, setIsWineDetailsCanvasOpened] = useState(false);
  const [isWineListsPanelOpen, setIsWineListsPanelOpen] = useState(false);
  const { data: memberShoppingCartData } = useQuery<MemberShoppingCart>(GET_MEMBER_SHOPPING_CART);

  const { wineType } = wine || {};
  const { wineClass, id: wineTypeId } = wineType || {};
  const { id: wineClassId } = wineClass || {};
  const baseColor = getWineBaseColor(wineClassId!, wineTypeId!);
  useTopInsetBg(baseColor);
  const bottleImage = wine?.product?.productPhotos?.[0]?.photoLarge || cellarBottleImg;

  const handleCloseTray = () => {
    setIsShowing(false);
    setTimeout(() => onCloseTray(), 200);
  };

  const renderTrayContent = () => (
    <GridHolder>
      <ImageSection>
        <CellarBodyCloseBtnImageSection ghost noBorder onClick={handleCloseTray} icon={backArrow} />
        <CellarBottleHolder baseColor={baseColor}>
          <RotatingBottleBox imgUrl={bottleImage} rotateMobile includeCloseButtonOffset />
        </CellarBottleHolder>
      </ImageSection>

      <BodySection>
        <BodySectionContent>
          <WineTagsShareAndBookmark
            tagsBgColor={baseColor}
            handleWineListsPanelChange={() => setIsWineListsPanelOpen(!isWineListsPanelOpen)}
            wine={wine}
          />

          <Title tag={1} fontSecondary>
            {wine?.product?.name ?? ""}
          </Title>

          <CellarBtnCta full lg ghost onClick={() => setIsWineDetailsCanvasOpened(true)}>
            {t("myCellar:wineDetailsEtc")}
          </CellarBtnCta>

          <BuyAgainButtons
            isInBoxAndCartTray={isInBoxAndCartTray}
            wine={wine}
            baseColor={baseColor}
            bottleImage={bottleImage}
          />
        </BodySectionContent>
      </BodySection>
    </GridHolder>
  );

  return (
    <OffCanvasTray
      setOverflowHidden={false}
      isOffcanvasOpened
      outSideClickHandler={() => handleCloseTray()}
      isMaskVisible={false}
    >
      <AnimatingTrayContainer isOpened hide={!isShowing}>
        {renderTrayContent()}
        {isWineDetailsCanvasOpened && (
          <WineDetailsTray
            memberShoppingCartQuery={memberShoppingCartData}
            handleToggleBottleInfoTray={() => setIsWineDetailsCanvasOpened(false)}
            slug={wine?.product?.slug}
          />
        )}
        {isWineListsPanelOpen && (
          <WineListsSidePanel
            handleOffCanvasChange={() => setIsWineListsPanelOpen(!isWineListsPanelOpen)}
            memberId={memberId}
            wine={wine}
            history={history}
          />
        )}
      </AnimatingTrayContainer>
    </OffCanvasTray>
  );
};

export default WineCellarVintageOptionTray;
